import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  BlockScopedThreadsForSectionQuery,
  BlockScopedThreadsForSectionQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faComment, faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  CurrentPostContext,
  PostType,
  useMessageTeacher,
} from "../../providers/MessageTeacherProvider";
import { BLOCK_SCOPED_THREADS_FOR_SECTION_QUERY } from "../../queries/BlockScopedThreadsForSectionQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import NewThreadView from "./NewThreadView";
import ThreadDetailView from "./ThreadDetailView";
import ThreadListItem from "./ThreadListItem";

interface PendingPost {
  postUid: string;
  postType: PostType;
  postTitle: string;
  source: "post" | "emptyState";
}

interface MessageTeacherButtonProps {
  sectionUid: string;
}

function contextToPendingPost(
  context: CurrentPostContext,
  source: PendingPost["source"],
): PendingPost {
  return {
    postUid: context.postUid,
    postType: context.postType,
    postTitle: context.postTitle,
    source,
  };
}

export default function MessageTeacherButton({
  sectionUid,
}: MessageTeacherButtonProps) {
  const { t } = useTranslation("learnerApp\\MessageTeacherButton");
  const isMobile = useIsMobile();
  const currentLearner = useCurrentLearner();
  const { registerOpenForPost, currentPostContext } = useMessageTeacher();
  const [open, setOpen] = useState(false);
  const [selectedThreadUid, setSelectedThreadUid] = useState<string | null>(
    null,
  );
  const [pendingPost, setPendingPost] = useState<PendingPost | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    loading: threadsLoading,
    refetch,
  } = useQueryWithPreviousData<
    BlockScopedThreadsForSectionQuery,
    BlockScopedThreadsForSectionQueryVariables
  >(BLOCK_SCOPED_THREADS_FOR_SECTION_QUERY, {
    variables: {
      sectionUid,
      searchQuery: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
  });

  const unreadCount = data?.section?.unreadBlockScopedThreadsCount ?? 0;
  const leader = data?.section?.leader;
  const threads = useMemo(
    () => data?.section?.blockScopedThreads ?? [],
    [data?.section?.blockScopedThreads],
  );
  const sectionEndTime = data?.section?.end_time ?? null;

  const openForPost = useCallback(
    (postUid: string, postType: PostType, postTitle: string) => {
      const match = threads.find(thread => {
        if (postType === "assignmentPost") {
          return thread.learningAssignmentClassPost?.uid === postUid;
        }
        return thread.classPost?.uid === postUid;
      });

      if (match) {
        setSelectedThreadUid(match.uid);
        setPendingPost(null);
      } else {
        setSelectedThreadUid(null);
        setPendingPost({ postUid, postType, postTitle, source: "post" });
      }
      setOpen(true);
    },
    [threads],
  );

  useEffect(() => {
    registerOpenForPost(openForPost);
  }, [registerOpenForPost, openForPost]);

  const handleClose = () => {
    setOpen(false);
    setSelectedThreadUid(null);
    setPendingPost(null);
    setSearchInput("");
    setDebouncedSearch("");
  };

  const handleThreadCreated = (threadUid: string) => {
    setPendingPost(null);
    setSelectedThreadUid(threadUid);
    refetch();
  };

  const selectedThread = selectedThreadUid
    ? threads.find(t => t.uid === selectedThreadUid)
    : null;
  const teacherName = leader?.name?.trim() || t("Teacher");
  const showEmptyConversationsState =
    !threadsLoading && threads.length === 0 && !debouncedSearch;

  const sectionInfo = { uid: sectionUid, end_time: sectionEndTime };
  const learnerInfo = {
    uid: currentLearner!.uid,
    name: currentLearner?.name ?? "",
    avatar: currentLearner?.avatar ?? null,
  };

  const handleStartThreadFromCurrentPost = () => {
    if (!currentPostContext) {
      return;
    }
    setPendingPost(contextToPendingPost(currentPostContext, "emptyState"));
  };

  return (
    <>
      {isMobile ? (
        <Badge badgeContent={unreadCount} color="error">
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Icon icon={faComment} sx={{ fontSize: "1.5rem" }} />
          </Button>
        </Badge>
      ) : (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 8 }}>
            <UserHeadshotImage user={leader} size={24} />
          </Badge>
          <Box component="span">{t("Message Teacher")}</Box>
        </Button>
      )}
      <Drawer
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme: Theme) => ({
            width: 400,
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              width: "85%",
            },
          }),
        }}
      >
        {selectedThreadUid ? (
          <ThreadDetailView
            threadUid={selectedThreadUid}
            section={sectionInfo}
            learner={learnerInfo}
            leader={leader ?? null}
            postTitle={
              selectedThread?.classPost?.title ??
              selectedThread?.learningAssignmentClassPost?.title
            }
            searchQuery={debouncedSearch}
            onBack={() => setSelectedThreadUid(null)}
            onClose={handleClose}
          />
        ) : pendingPost ? (
          <NewThreadView
            section={sectionInfo}
            {...(pendingPost.postType === "classPost"
              ? { classPostUid: pendingPost.postUid }
              : { learningAssignmentClassPostUid: pendingPost.postUid })}
            postTitle={
              pendingPost.source === "post" ? pendingPost.postTitle : undefined
            }
            learner={learnerInfo}
            leader={leader ?? null}
            hideBackButton={pendingPost.source === "emptyState"}
            onBack={() => setPendingPost(null)}
            onClose={handleClose}
            onThreadCreated={handleThreadCreated}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                p: 12,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <UserHeadshotImage
                user={leader}
                size={44}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                  {leader?.name ?? t("Teacher")}
                </Typography>
                {Boolean(sectionEndTime) && (
                  <Typography variant="caption" color="text.secondary">
                    {t("Teacher access ends {{date}}", {
                      date: dayjs(sectionEndTime).format("MMM D, YYYY"),
                    })}
                  </Typography>
                )}
              </Box>
              <IconButton
                aria-label={t("Close messages")}
                icon={faTimes}
                variant="text"
                onClick={handleClose}
              />
            </Box>
            {!showEmptyConversationsState && (
              <Box
                sx={{
                  py: 8,
                  px: 12,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("Search all messages")}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon
                          icon={faSearch}
                          sx={{ color: "text.secondary", fontSize: "1rem" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment:
                      threadsLoading && debouncedSearch ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 20 },
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
              {threadsLoading && threads.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : showEmptyConversationsState ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 24,
                    gap: 12,
                  }}
                >
                  <Icon
                    icon={faComment}
                    sx={{ color: "neutral.300", fontSize: "4rem" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {t("No conversations yet")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 300 }}
                  >
                    {t(
                      'Explore a lesson and use the "Ask a question" button below your lesson to start chatting with your teacher.',
                    )}
                  </Typography>
                  {currentPostContext && (
                    <Button
                      variant="outlined"
                      onClick={handleStartThreadFromCurrentPost}
                      sx={{
                        mt: 8,
                        borderColor: "neutral.200",
                        color: "text.primary",
                        borderRadius: "1.4rem",
                        textTransform: "none",
                      }}
                    >
                      {t("Message {{teacherName}} directly", {
                        teacherName,
                      })}
                    </Button>
                  )}
                </Box>
              ) : threads.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("No messages found")}
                  </Typography>
                </Box>
              ) : (
                threads.map(thread => (
                  <ThreadListItem
                    key={thread.uid}
                    thread={thread}
                    onClick={() => setSelectedThreadUid(thread.uid)}
                  />
                ))
              )}
            </Box>
          </>
        )}
      </Drawer>
    </>
  );
}
